import { CENTER_INDEX, COLORS, COLOR_ORDER, FACE_CONFIG, FACE_ORDER } from './cube-config.js';
import { CubeState } from './cube-state.js';
import { validateCubeState } from './validation.js';
import {
  APP_VERSION,
  COLOR_NUMBERING,
  STATE_TRANSFER_SCHEMA_VERSION,
  assertArray,
  assertModernMetadata,
  assertPlainObject,
  cloneStickers,
  faceMatrices,
  parseTransferData,
  sameArray,
  validateColorNumbering,
  validateMatrices,
} from './state-transfer.js';

export const STATE_TRANSFER_VERSION_3X3 = STATE_TRANSFER_SCHEMA_VERSION;
export const COLOR_NUMBERING_3X3 = COLOR_NUMBERING;

function normalizedFacelets(stickers) {
  return FACE_ORDER.flatMap((face) => stickers[face].map((color) => (
    color === null ? null : COLOR_NUMBERING[color]
  )));
}

function validateOriginalStickers(flat) {
  assertArray(flat, 54, 'originalStickers');
  const counts = Object.fromEntries(COLOR_ORDER.map((color) => [color, 0]));
  for (let index = 0; index < flat.length; index += 1) {
    const color = flat[index];
    if (color !== null && !COLOR_ORDER.includes(color)) {
      throw new TypeError(`originalStickers[${index}] 包含未知颜色：${color}`);
    }
    if (color !== null) counts[color] += 1;
  }
  for (const color of COLOR_ORDER) {
    if (counts[color] > 9) throw new TypeError(`${COLORS[color].name}贴纸数量不能超过 9，当前为 ${counts[color]}`);
  }
}

function validateNormalizedFacelets(facelets) {
  assertArray(facelets, 54, 'normalizedFacelets');
  for (let index = 0; index < facelets.length; index += 1) {
    const value = facelets[index];
    if (value !== null && (!Number.isInteger(value) || value < 0 || value > 5)) {
      throw new TypeError(`normalizedFacelets[${index}] 必须为 null 或 0–5 的整数`);
    }
  }
}

function progressFor(stickers) {
  const filledStickers = FACE_ORDER.flatMap((face) => stickers[face]).filter((color) => color !== null).length;
  const manualFilled = Math.max(filledStickers - 6, 0);
  return {
    filledStickers,
    manualFilled,
    totalStickers: 54,
    requiredManualStickers: 48,
    complete: filledStickers === 54,
  };
}

function validateProgress(progress, expected) {
  assertPlainObject(progress, 'progress');
  for (const [key, value] of Object.entries(expected)) {
    if (progress[key] !== value) throw new TypeError(`progress.${key} 与贴纸数据不一致`);
  }
}

export function exportCubeStateJson3x3(stickers) {
  const state = new CubeState({ stickers });
  const originalStickers = FACE_ORDER.flatMap((face) => state.stickers[face]);
  const normalized = normalizedFacelets(state.stickers);
  return {
    schemaVersion: STATE_TRANSFER_SCHEMA_VERSION,
    version: STATE_TRANSFER_VERSION_3X3,
    appVersion: APP_VERSION,
    puzzleSize: 3,
    faceOrder: [...FACE_ORDER],
    originalStickers,
    normalizedFacelets: normalized,
    urfdlbMatrices: faceMatrices(normalized, 3),
    colorNumbering: { ...COLOR_NUMBERING },
    progress: progressFor(state.stickers),
    exportedAt: new Date().toISOString(),
  };
}

export function createDiagnosticData3x3(stickers) {
  const exported = exportCubeStateJson3x3(stickers);
  const validation = validateCubeState(stickers);
  return {
    ...exported,
    kind: 'cube-path-3x3-diagnostic',
    formalValidation: structuredClone(validation),
  };
}

export function importCubeStateJson3x3(serialized) {
  const data = parseTransferData(serialized);
  assertPlainObject(data, '导入数据');
  assertModernMetadata(data);
  if (data.version !== STATE_TRANSFER_VERSION_3X3) throw new TypeError(`不支持的3×3状态版本：${data.version ?? '缺失'}`);
  if (data.puzzleSize !== 3) throw new TypeError('导入文件不是3×3魔方状态，已拒绝覆盖当前数据');
  if (!sameArray(data.faceOrder ?? [], FACE_ORDER)) throw new TypeError('faceOrder 必须严格为 U,R,F,D,L,B');
  validateOriginalStickers(data.originalStickers);
  validateNormalizedFacelets(data.normalizedFacelets);
  validateColorNumbering(data.colorNumbering);

  const stickers = Object.fromEntries(FACE_ORDER.map((face, faceIndex) => [
    face,
    data.originalStickers.slice(faceIndex * 9, faceIndex * 9 + 9),
  ]));
  for (const face of FACE_ORDER) {
    if (stickers[face][CENTER_INDEX] !== FACE_CONFIG[face].color) {
      throw new TypeError(`${face} 面中心块必须为${COLORS[FACE_CONFIG[face].color].name}，导入已取消`);
    }
  }
  const expectedNormalized = normalizedFacelets(stickers);
  if (!sameArray(data.normalizedFacelets, expectedNormalized)) {
    throw new TypeError('normalizedFacelets 与原始54贴纸不一致');
  }
  validateMatrices(data.urfdlbMatrices, expectedNormalized, 3);
  validateProgress(data.progress, progressFor(stickers));

  const formalValidation = validateCubeState(stickers);
  if (data.progress.complete && !formalValidation.valid) {
    throw new TypeError(`完整状态未通过正式校验：${formalValidation.errors[0]?.message ?? '状态不合法'}`);
  }
  return { state: new CubeState({ stickers: cloneStickers(stickers) }), formalValidation };
}
